/**
 * Toast helpers over @mantine/notifications.
 *
 * WHY THIS EXISTS. Two recurring bugs motivated these wrappers:
 *   1. Eden Treaty / Better-Auth surface failures as `{ status, value }` /
 *      `{ error: { message } }` envelopes — NOT `Error` instances. So the
 *      ubiquitous `err instanceof Error ? err.message : String(err)` pattern
 *      silently drops the real message and renders `[object Object]` in a toast.
 *      `notifyError` funnels every thrown value through `extractErrorMessage`,
 *      which unwraps those envelopes (see `lib/api/errors.ts`).
 *   2. The old `MSG_ERROR_TITLE = "Error"` constant was a raw English string,
 *      never translated. The default titles here are Lingui `msg` descriptors
 *      resolved against the shared `i18n` instance (the same one `<I18nProvider>`
 *      wraps in main.tsx), so they follow the active app locale.
 *
 * Call sites keep control of translatable copy: pass an already-resolved
 * `i18n._(msg\`...\`)` string as `title` (error) or `message`/`title` (success).
 * Omit the title to get the localized default.
 */

import { msg } from "@lingui/core/macro"
import { Button } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { extractErrorMessage } from "./api/errors"
import { reportClientError } from "./client-error-reporting"
import { i18n } from "./i18n"

export interface INotifyWithUndoOptions {
  /** Already-translated body: what happened. An Undo button is appended. */
  message: string
  onUndo: () => void
  /** Milliseconds before the toast closes. Defaults to 8 seconds. */
  autoClose?: number
}

/**
 * Show a danger toast for a thrown value of any shape. `err` is coerced to a
 * human-readable string via `extractErrorMessage` (Eden/Better-Auth envelopes
 * included), so it never renders `[object Object]`. `title` defaults to a
 * localized "Error".
 */
export function notifyError(err: unknown, title?: string): void {
  notifications.show({
    title: title ?? i18n._(msg`Error`),
    message: extractErrorMessage(err),
    color: "danger",
  })
  reportClientError(err, { eventType: "client_toast_error", title })
}

/**
 * Show a success toast. `message` is the (already-translated) body; `title`
 * defaults to a localized "Success".
 */
export function notifySuccess(message: string, title?: string): void {
  notifications.show({
    title: title ?? i18n._(msg`Success`),
    message,
    color: "success",
  })
}

/**
 * Success toast with an Undo action. `message` is already translated and is
 * the whole toast — what happened, plus the Undo button. No separate title:
 * a title like "You can Undo" hid the actual change.
 */
export function notifyWithUndo({ message, onUndo, autoClose = 8000 }: INotifyWithUndoOptions): void {
  notifications.show({
    message: (
      <>
        {message}{" "}
        <Button
          size="xs"
          variant="light"
          onClick={() => {
            onUndo()
          }}
        >
          {i18n._(msg`Undo`)}
        </Button>
      </>
    ),
    color: "success",
    autoClose,
  })
}
